import React from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Fieldset } from "primereact/fieldset";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import agent from "../../../agent";
import { MILE_TYPES } from "../TariffPlans/mileTypes";
import { emptyMiles, milesOf, isActive } from "./clientRules";

/**
 * Тарифные карты и дисконт по плечам для юрлица slq.kz — блок в карточке клиента.
 *
 * Те же правила, что у партнёров (nsi.partner_discount), только субъект другой.
 * Приоритет у клиента: если карта назначена и ему, и приведшему его партнёру,
 * на этом плече считается клиентская — она адреснее.
 *
 * Клиент опознаётся по X-User-Id, который присылает калькулятор slq.kz. Через
 * партнёрскую платформу тот же клиент приходит неопознанным, и там действуют
 * партнёрские настройки.
 */
const ClientTariffs = ({ clientGuid, onMessage }) => {
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [plans, setPlans] = React.useState([]);
    const [saved, setSaved] = React.useState(emptyMiles());
    const [miles, setMiles] = React.useState(emptyMiles());
    const [active, setActive] = React.useState(true);

    const notify = (severity, summary, detail) => onMessage?.(severity, summary, detail);

    React.useEffect(() => {
        agent.TariffPlans.list()
            .then((res) => setPlans(Array.isArray(res) ? res : []))
            .catch(() => {});
    }, []);

    const load = React.useCallback(() => {
        if (!clientGuid) return;
        setLoading(true);
        agent.PartnerDiscounts.list()
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.content || []);
                const loaded = milesOf(list, clientGuid);
                setSaved(loaded);
                setMiles(loaded);
                setActive(isActive(list, clientGuid));
            })
            .catch((err) => notify("error", "Ошибка", readError(err)))
            .finally(() => setLoading(false));
    }, [clientGuid]);

    React.useEffect(() => { load(); }, [load]);

    const setMile = (mileType, patch) =>
        setMiles((prev) => ({ ...prev, [mileType]: { ...prev[mileType], ...patch } }));

    /**
     * Сервер хранит строку на плечо, поэтому сохранение расходится на четыре
     * запроса: заданное плечо пишется, очищенное — удаляется. Пустая строка
     * «без карты и без процента» не хранится: она ничего не делает.
     */
    const onSave = () => {
        setSaving(true);
        const calls = MILE_TYPES.map(({ value: mileType }) => {
            const mile = miles[mileType] ?? {};
            const filled = mile.percent != null || mile.tariffPlanId != null;

            if (filled) {
                return agent.PartnerDiscounts.saveClient({
                    clientGuid,
                    mileType,
                    percent: mile.percent ?? null,
                    tariffPlanId: mile.tariffPlanId ?? null,
                    trustRequestPercent: false,
                    active,
                });
            }
            // Удаляем только то, что было: иначе получим ошибку на пустом плече.
            const existing = saved[mileType] ?? {};
            return existing.percent != null || existing.tariffPlanId != null
                ? agent.PartnerDiscounts.removeClient(clientGuid, mileType)
                : Promise.resolve();
        });

        Promise.all(calls)
            .then(() => {
                notify("success", "Запрос выполнен",
                    "Тарифные карты клиента сохранены, новые цены действуют сразу");
                load();
            })
            .catch((err) => notify("error", "Ошибка", readError(err)))
            .finally(() => setSaving(false));
    };

    const onToggleActive = (next) => {
        setActive(next);
        agent.PartnerDiscounts.setClientActive(clientGuid, next)
            .then(() => notify("success", "Запрос выполнен",
                next ? "Настройки включены" : "Настройки выключены"))
            .catch((err) => {
                setActive(!next);
                notify("error", "Ошибка", readError(err));
            });
    };

    const plansForMile = (mileType) =>
        plans
            .filter((plan) => plan.mileType === mileType)
            .map((plan) => ({
                label: plan.isActive ? plan.name : `${plan.name} (выключена)`,
                value: plan.id,
            }));

    const hasAnything = MILE_TYPES.some(({ value }) => {
        const mile = saved[value] ?? {};
        return mile.percent != null || mile.tariffPlanId != null;
    });

    return (
        <Fieldset legend="Тарифные карты и дисконт по плечам">
            <Message severity="info" style={{ display: "block", marginBottom: 10 }}
                     text="Приоритет у тарифной карты: покрыла она направление — цена из карты, скидка не применяется. Направления, которого в карте нет, считаются обычным тарифом — там и сработает процент. Действует в расчётах на slq.kz; через партнёрскую платформу клиент приходит неопознанным." />

            <div className="p-fluid p-formgrid p-grid">
                {MILE_TYPES.map(({ value, label }) => (
                    <React.Fragment key={value}>
                        <div className="p-field p-col-12 p-md-4">
                            <label htmlFor={`plan-${value}`}>{label}</label>
                            <Dropdown inputId={`plan-${value}`} value={miles[value]?.tariffPlanId ?? null}
                                      options={plansForMile(value)} showClear filter
                                      placeholder="Стандартный тариф" disabled={loading}
                                      onChange={(e) => setMile(value, { tariffPlanId: e.value ?? null })} />
                        </div>
                        <div className="p-field p-col-12 p-md-3">
                            <label htmlFor={`percent-${value}`}>Скидка, %</label>
                            <InputNumber inputId={`percent-${value}`} value={miles[value]?.percent ?? null}
                                         mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                         min={0} max={99.99} placeholder="без скидки" disabled={loading}
                                         onValueChange={(e) => setMile(value, { percent: e.value ?? null })} />
                        </div>
                        <div className="p-col-12 p-md-5" style={{ alignSelf: "center", color: "#6b7280" }}>
                            {miles[value]?.tariffPlanId
                                ? "Цена по карте; процент сработает на направлениях, которых в ней нет."
                                : "Обычный расчёт со скидкой, если процент задан."}
                        </div>
                    </React.Fragment>
                ))}
            </div>

            <div className="p-d-flex p-jc-between p-ai-center" style={{ marginTop: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Тумблер переключает клиента целиком: полпрофиля означало бы
                        полмаршрута со скидкой — состояние, которого никто не заказывал. */}
                    <InputSwitch checked={active} disabled={!hasAnything || loading}
                                 onChange={(e) => onToggleActive(e.value)} />
                    <span style={{ color: hasAnything ? undefined : "#9ca3af" }}>
                        {hasAnything ? "Настройки включены" : "Настроек пока нет"}
                    </span>
                </div>
                {/* width: auto — блок живёт в карточке клиента, а там в корне p-fluid,
                    который тянет кнопку на всю ширину строки. */}
                <Button label="Сохранить" icon="pi pi-check" style={{ width: "auto" }}
                        loading={saving} disabled={loading} onClick={onSave} />
            </div>
        </Fieldset>
    );
};

const readError = (err) =>
    (typeof err === "string" ? err : null)
    || err?.response?.body?.messages?.[0]
    || err?.response?.body?.message
    || agent.handleError(err);

export default ClientTariffs;
